import { defineElement, FC, onDidLoad, useProp } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { CountryModel, countryQuery } from '@bpd-library/utilities';
import { GERMANY_ID, NETHERLANDS_ID } from '@constants/countries';
import { useCountryPostcodeValidator, useTranslate } from '@utilities/hooks';
import { isGermany } from '@utilities/is-germany';

import { SelectElement, SelectOption } from '../select/select';

const SelectCountry: FC = ({ element }) => {
    const [name] = useProp<string>('name');
    const [id] = useProp<string>('id');
    const [label] = useProp<string | Text>('label', useTranslate('SelectCountry'));
    const [preFilledCountry] = useProp<string | null>('preFilledCountry', null);

    const defaultValue = isGermany() ? GERMANY_ID : NETHERLANDS_ID;
    useCountryPostcodeValidator({ id: defaultValue });

    onDidLoad(async () => {
        const countries = await countryQuery();

        render(countries);
    });

    async function render(countries: CountryModel[]) {
        const options: SelectOption[] = await Promise.all(
            countries
                .filter(({ englishName }) => !!englishName)
                .map(async (country) => {
                    const { id, englishName } = country;

                    return {
                        value: id,
                        label: await useTranslate(englishName!),
                    };
                }),
        );

        element.innerHTML = '';

        element.append(
            <SelectElement
                hook="select-country-input"
                name={name}
                id={id}
                label={label}
                options={options}
                value={preFilledCountry || defaultValue}
                readonly={!!preFilledCountry}
            />,
        );
    }

    return <div></div>;
};

SelectCountry.props = {
    name: {
        type: String,
        required: true,
    },
    id: {
        type: String,
        required: true,
    },
    label: {
        type: String,
    },
    hook: {
        type: String,
        required: true,
    },
    preFilledCountry: {
        type: String,
        required: false,
    },
};

defineElement('select-country', SelectCountry);
